
import React, { useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ImageZoom } from './ImageZoom';
import { useSignedMediaUrl } from '@/hooks/useSignedMediaUrl';
import { Skeleton } from '@/components/ui/skeleton';
import { Images } from 'lucide-react';

interface MediaMessage {
  id: string;
  media_url: string;
  created_at: string;
}

interface ChatMediaGalleryProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  mediaMessages: MediaMessage[];
}

const MediaImageItem: React.FC<{ message: MediaMessage }> = ({ message }) => {
  const { signedUrl, isLoading, error } = useSignedMediaUrl({
    mediaUrl: message.media_url,
    contentType: 'image'
  });

  if (isLoading || !signedUrl) {
    return <Skeleton className="w-full aspect-square rounded-lg" />;
  }

  if (error) {
    return (
      <div className="w-full aspect-square rounded-lg bg-gray-800 flex items-center justify-center">
        <span className="text-gray-400 text-xs">Erro ao carregar</span>
      </div>
    );
  }

  return (
    <ImageZoom src={signedUrl} alt="Imagem compartilhada">
      <img
        src={signedUrl}
        alt="Imagem compartilhada"
        className="w-full aspect-square object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
      />
    </ImageZoom>
  );
};

export const ChatMediaGallery: React.FC<ChatMediaGalleryProps> = ({
  isOpen,
  onOpenChange,
  mediaMessages
}) => {
  const sortedMediaMessages = useMemo(() => {
    return [...mediaMessages].sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [mediaMessages]);

  // CSS styles for the red close button
  const closeButtonStyles = `
    [data-radix-dialog-content] button[data-radix-dialog-close] {
      width: 48px !important;
      height: 48px !important;
      background-color: #ea384c !important;
      border-radius: 9999px !important;
      opacity: 1 !important;
      color: white !important;
      transition: background-color 0.2s ease !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
    }
    
    [data-radix-dialog-content] button[data-radix-dialog-close]:hover {
      background-color: rgba(234, 56, 76, 0.9) !important;
    }
    
    [data-radix-dialog-content] button[data-radix-dialog-close] svg {
      width: 24px !important;
      height: 24px !important;
    }
  `;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] bg-black/95 border-white/10">
        {/* Custom styles for the red close button - moved to CSS module or component styles */}
        
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Images className="h-5 w-5" />
            Mídias Compartilhadas ({sortedMediaMessages.length})
          </DialogTitle>
          <DialogDescription className="text-white/70">
            Veja todas as fotos e vídeos compartilhados nesta conversa
          </DialogDescription>
        </DialogHeader>
        
        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          {sortedMediaMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-white/60">
              <Images className="h-12 w-12 mb-4" />
              <p className="text-lg font-medium">Nenhuma mídia compartilhada</p>
              <p className="text-sm">As fotos enviadas nesta conversa aparecerão aqui</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 p-2">
              {sortedMediaMessages.map((message) => (
                <MediaImageItem key={message.id} message={message} />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
